import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, EyeOff, Eye, Reply } from "lucide-react";
import { BRAND, errMsg } from "./constants";

export function Stars({ value, size = 14, onPick, tid }) {
  return (
    <span className="inline-flex gap-0.5" data-testid={tid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} onClick={onPick ? () => onPick(n) : undefined} data-testid={tid && onPick ? `${tid}-${n}` : undefined}
          className={onPick ? "cursor-pointer transition-transform hover:scale-110" : ""}
          style={{ width: size, height: size, color: "#F59E0B", fill: n <= Math.round(value) ? "#F59E0B" : "transparent" }} />
      ))}
    </span>
  );
}

export function ReviewItem({ rv, children }) {
  return (
    <div data-testid={`review-${rv.id}`} className="py-3 border-t text-sm" style={{ borderColor: BRAND.line, opacity: rv.hidden ? 0.5 : 1 }}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold">{rv.guest_name} {rv.hidden && <span className="chip text-[10px]">hidden by {rv.hidden_by}</span>}</span>
        <Stars value={rv.rating} />
      </div>
      {rv.text && <div className="mt-1">{rv.text}</div>}
      {rv.reply && <div className="mt-1 text-xs rounded-lg p-2" style={{ background: "#F0FDF4", color: "#14532D" }} data-testid={`review-reply-${rv.id}`}><b>Owner:</b> {rv.reply}</div>}
      {children}
    </div>
  );
}

export function ReviewsPanel({ pid, authFetch, API, isOwner }) {
  const [d, setD] = useState({ items: [], rating_avg: 0, rating_count: 0 });
  const load = useCallback(() => authFetch({ url: `${API}/homestay/properties/${pid}/reviews` }).then((r) => setD(r.data)).catch(() => {}), [pid, authFetch, API]);
  useEffect(() => { load(); }, [load]);

  const patch = async (rv, data, msg) => {
    try { await authFetch({ method: "patch", url: `${API}/homestay/reviews/${rv.id}`, data }); toast.success(msg); load(); }
    catch (e) { toast.error(errMsg(e, "Could not update review")); }
  };
  const reply = (rv) => {
    const t = window.prompt("Public reply", rv.reply || "Thank you for staying with us!");
    if (t !== null) patch(rv, { reply: t }, "Reply posted");
  };

  return (
    <div data-testid="reviews-panel" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold" style={{ color: BRAND.green }}>Guest reviews</div>
        <span className="text-sm font-bold inline-flex items-center gap-1.5" data-testid="reviews-avg"><Stars value={d.rating_avg} /> {d.rating_avg || "–"} ({d.rating_count})</span>
      </div>
      {d.items.length === 0 && <div className="text-xs mt-2" style={{ color: BRAND.muted }}>No reviews yet — checked-out guests can rate their stay.</div>}
      <div className="max-h-[28rem] overflow-y-auto">
        {d.items.map((rv) => (
          <ReviewItem key={rv.id} rv={rv}>
            {isOwner && (
              <div className="mt-2 flex gap-2">
                <button data-testid={`review-reply-btn-${rv.id}`} onClick={() => reply(rv)} className="rounded-full border px-2.5 py-1 text-[11px] font-bold inline-flex items-center gap-1" style={{ borderColor: BRAND.green, color: BRAND.green }}><Reply className="w-3 h-3" /> Reply</button>
                <button data-testid={`review-hide-btn-${rv.id}`} onClick={() => patch(rv, { hidden: !rv.hidden }, rv.hidden ? "Review visible" : "Review hidden")} className="rounded-full border px-2.5 py-1 text-[11px] font-bold inline-flex items-center gap-1" style={{ borderColor: BRAND.muted, color: BRAND.muted }}>
                  {rv.hidden ? <><Eye className="w-3 h-3" /> Unhide</> : <><EyeOff className="w-3 h-3" /> Hide</>}
                </button>
              </div>
            )}
          </ReviewItem>
        ))}
      </div>
    </div>
  );
}
